import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { fetchSupplierPortfolio, fetchSupplierScores } from '../services/analyticsService';
import { novaGenerateReport, novaDownloadReport } from '../services/novaService';
import './Suppliers.css';

const API_SCOPE = 'api://6069b616-c7f2-4978-9f9c-180ec12f1844/access_as_user';

const CHART_CARD = { background: 'white', borderRadius: 14, border: '1px solid #e3e8ef', padding: '20px 16px', display: 'flex', flexDirection: 'column' };

function NovaInsight({ text }) {
  if (!text) return null;
  return (
    <div style={{ ...CHART_CARD, padding: '14px 18px', marginBottom: 20, flexDirection: 'row', alignItems: 'flex-start', gap: 10 }}>
      <span className="ai-badge" style={{ flexShrink: 0, marginTop: 1 }}>Nova</span>
      <div style={{ fontSize: 13, color: 'var(--navy)', lineHeight: 1.55 }}>{text}</div>
    </div>
  );
}

const fmtDate = (d) => {
  if (!d) return '\u2014';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '\u2014';
  return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

export default function MyFavoriteSuppliers() {
  const { instance: msalInstance } = useMsal();
  const [favNames, setFavNames] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ col: 'cumulative', dir: 'desc' });
  const [scoreMap, setScoreMap] = useState({});
  const [exportingPptx, setExportingPptx] = useState(false);
  const [exportingXlsx, setExportingXlsx] = useState(false);
  const [exportErr, setExportErr] = useState('');

  // Fetch favorites list
  useEffect(() => {
    (async () => {
      try {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length === 0) return;
        let token;
        try { token = (await msalInstance.acquireTokenSilent({ scopes: [API_SCOPE], account: accounts[0] })).accessToken; }
        catch { token = (await msalInstance.acquireTokenPopup({ scopes: [API_SCOPE] })).accessToken; }
        const r = await fetch(`${import.meta.env.VITE_API_URL}/api/favorite-suppliers`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) {
          const d = await r.json();
          setFavNames(new Set(d.suppliers || []));
        } else {
          setFavNames(new Set());
        }
      } catch {
        setFavNames(new Set());
      }
    })();
  }, [msalInstance]);

  // Load portfolio data + scores
  useEffect(() => {
    if (favNames === null) return;
    setLoading(true); setErr('');
    Promise.all([
      fetchSupplierPortfolio(msalInstance),
      fetchSupplierScores(msalInstance).catch(() => ({ suppliers: [] })),
    ])
      .then(([d, sc]) => {
        setData(d);
        const m = {};
        for (const s of (sc.suppliers || [])) m[s.supplier_name] = s.total_score;
        setScoreMap(m);
      })
      .catch(() => setErr('Failed to load supplier data'))
      .finally(() => setLoading(false));
  }, [msalInstance, favNames]);

  const loadDetail = useCallback((name) => {
    setSelectedSupplier(name);
    setDetailLoading(true);
    fetchSupplierPortfolio(msalInstance, { supplier: name })
      .then(d => setDetail(d))
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  }, [msalInstance]);

  const goBack = () => { setSelectedSupplier(null); setDetail(null); };

  // Filter to favorites only
  const filtered = useMemo(() => {
    if (!data?.suppliers || !favNames) return [];
    let arr = data.suppliers
      .filter(r => favNames.has(r.supplierName))
      .map(r => ({ ...r, score: scoreMap[r.supplierName] ?? null }));
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      arr = arr.filter(r => r.supplierName.toLowerCase().includes(s));
    }
    const sorted = [...arr];
    sorted.sort((a, b) => {
      let av = a[sort.col], bv = b[sort.col];
      if (av == null) return 1; if (bv == null) return -1;
      if (typeof av === 'number') return sort.dir === 'desc' ? bv - av : av - bv;
      return sort.dir === 'desc' ? String(bv).localeCompare(String(av)) : String(av).localeCompare(String(bv));
    });
    return sorted;
  }, [data, search, sort, scoreMap, favNames]);

  const toggleSort = (col) => setSort(prev => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'desc' });

  const handleExport = useCallback(async (format) => {
    const setLoading2 = format === 'pptx' ? setExportingPptx : setExportingXlsx;
    setLoading2(true); setExportErr('');
    try {
      const meta = await novaGenerateReport(msalInstance, { reportType: 'supplier-portfolio', format, filters: { supplier: selectedSupplier } });
      const blob = await novaDownloadReport(msalInstance, meta.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = meta.filename || `supplier-portfolio.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { setExportErr('Export failed.'); }
    finally { setLoading2(false); }
  }, [msalInstance, selectedSupplier]);

  const SortTh = ({ col, label }) => {
    const active = sort.col === col;
    return (
      <th className="sup-sort-head" onClick={() => toggleSort(col)}>
        <span style={{ cursor: 'pointer' }}>{label}{active && <span className="sup-sort-ico">{sort.dir === 'asc' ? '\u25B2' : '\u25BC'}</span>}</span>
      </th>
    );
  };

  // DETAIL VIEW (reuses same layout as SupplierPortfolioEvolution)
  if (selectedSupplier) {
    return (
      <div className="sup-page">
        <div className="mb-3">
          <h2 style={{ fontWeight: 700, fontSize: 20, color: 'var(--navy)', margin: 0 }}>My favorite suppliers</h2>
          <div style={{ fontSize: 12, color: 'var(--t3)', marginTop: 2 }}>FocusOne &middot; Supplier Analytics</div>
        </div>
        <button type="button" onClick={goBack} style={{ background: 'none', border: 'none', color: 'var(--teal)', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: 0, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 4 }}>
          <i className="bi bi-arrow-left"></i> Back to favorites
        </button>
        {detailLoading && <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner-border spinner-border-sm me-2" role="status" />Loading...</div>}
        {!detailLoading && detail && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <h3 style={{ fontWeight: 700, fontSize: 18, color: 'var(--navy)', margin: 0 }}>{detail.supplier}</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {exportErr && <span style={{ fontSize: 11, color: 'var(--red)' }}>{exportErr}</span>}
                <button type="button" className="sup-badge teal-soft" disabled={exportingPptx} onClick={() => handleExport('pptx')} style={{ padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid #156082', cursor: exportingPptx ? 'wait' : 'pointer', background: exportingPptx ? '#e8e8e8' : undefined }}>{exportingPptx ? 'Generating...' : 'Download PowerPoint'}</button>
                <button type="button" className="sup-badge teal-soft" disabled={exportingXlsx} onClick={() => handleExport('xlsx')} style={{ padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid #156082', cursor: exportingXlsx ? 'wait' : 'pointer', background: exportingXlsx ? '#e8e8e8' : undefined }}>{exportingXlsx ? 'Generating...' : 'Download Excel'}</button>
              </div>
            </div>
            <NovaInsight text={detail.novaSummary} />
            {detail.timeline && detail.timeline.length > 0 && (
              <div style={{ ...CHART_CARD, marginBottom: 20 }}>
                <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: 15, color: 'var(--navy)', marginBottom: 12 }}>Portfolio Growth Over Time</div>
                <div style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={detail.timeline} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                      <XAxis dataKey="quarter" tick={{ fontSize: 10, fill: '#6c757d' }} interval={Math.max(0, Math.floor(detail.timeline.length / 12))} />
                      <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#6c757d' }} width={40} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#6c757d' }} width={40} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line yAxisId="left" type="monotone" dataKey="cumulativeProducts" name="Products" stroke="#0D5C4F" strokeWidth={2} dot={false} />
                      <Line yAxisId="right" type="monotone" dataKey="moleculeBreadth" name="Molecules (INN)" stroke="#0B3D6B" strokeWidth={2} dot={false} />
                      <Line yAxisId="right" type="monotone" dataKey="atcAreas" name="ATC Areas" stroke="#3B1F6E" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
            {detail.newAtcPerQuarter && detail.newAtcPerQuarter.length > 0 && (
              <div className="sup-table-wrap" style={{ marginBottom: 20 }}>
                <div style={{ padding: '10px 14px', fontWeight: 700, fontSize: 12, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>New ATC Areas Entered</div>
                <div className="sup-table-scroll" style={{ maxHeight: '40vh' }}>
                  <table className="sup-table"><thead><tr><th>Quarter</th><th>New Areas</th></tr></thead><tbody>
                    {detail.newAtcPerQuarter.map((r, i) => (<tr key={i}><td>{r.quarter}</td><td><div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>{r.areas.map((a, j) => <span key={j} className="sup-badge teal-soft">{a}</span>)}</div></td></tr>))}
                  </tbody></table>
                </div>
              </div>
            )}
            <div className="sup-table-wrap">
              <div style={{ padding: '10px 14px', fontWeight: 700, fontSize: 12, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>Quarter-by-Quarter</div>
              <div className="sup-table-scroll" style={{ maxHeight: '40vh' }}>
                <table className="sup-table"><thead><tr><th>Quarter</th><th>Net New</th><th>Cumulative</th><th>Molecules</th><th>ATC Areas</th></tr></thead><tbody>
                  {(detail.timeline || []).map((r, i) => (<tr key={i}><td>{r.quarter}</td><td><span className="sup-badge teal-soft">+{r.netNew}</span></td><td><strong>{r.cumulativeProducts}</strong></td><td>{r.moleculeBreadth}</td><td>{r.atcAreas}</td></tr>))}
                </tbody></table>
              </div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 8, fontStyle: 'italic' }}>Based on recorded product history.</div>
          </>
        )}
        {!detailLoading && !detail && (
          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e3e8ef', padding: '40px 24px', textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--t3)' }}>Failed to load detail for {selectedSupplier}.</p>
          </div>
        )}
      </div>
    );
  }

  // LIST VIEW
  return (
    <div className="sup-page">
      <div className="mb-3">
        <h2 style={{ fontWeight: 700, fontSize: 20, color: 'var(--navy)', margin: 0 }}>My favorite suppliers</h2>
        <div style={{ fontSize: 12, color: 'var(--t3)', marginTop: 2 }}>FocusOne &middot; Supplier Analytics</div>
      </div>

      <div className="sup-filters-row">
        <div>
          <div className="sup-field-label" style={{ marginBottom: 2, fontSize: 11, color: 'var(--t2)' }}>Search supplier</div>
          <input className="sup-input" style={{ width: 220 }} placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        {search && <button type="button" className="sup-clear-link" onClick={() => setSearch('')}>Clear</button>}
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner-border spinner-border-sm me-2" role="status" />Loading...</div>}
      {err && <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e3e8ef', padding: '40px 24px', textAlign: 'center' }}><p style={{ margin: 0, fontSize: 14, color: 'var(--red)' }}>{err}</p></div>}

      {!loading && !err && favNames && favNames.size === 0 && (
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e3e8ef', padding: '40px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.3 }}>{'\u2606'}</div>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--t3)' }}>No favorite suppliers yet &mdash; add some from the <a href="/suppliers-list" style={{ color: 'var(--teal)' }}>Suppliers List</a>.</p>
        </div>
      )}

      {!loading && !err && favNames && favNames.size > 0 && (
        <div className="sup-table-wrap">
          <div className="sup-table-scroll">
            <table className="sup-table">
              <thead>
                <tr>
                  <SortTh col="supplierName" label="Supplier" />
                  <SortTh col="score" label="Score" />
                  <SortTh col="cumulative" label="Products" />
                  <SortTh col="molecules" label="Molecules" />
                  <SortTh col="atcAreas" label="ATC Areas" />
                  <SortTh col="recentGrowth" label="Recent Growth" />
                  <th>First Seen</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: 30, color: 'var(--t3)' }}>No matching favorites.</td></tr>
                ) : (
                  filtered.map((r, i) => (
                    <tr key={i} style={{ cursor: 'pointer' }} onClick={() => loadDetail(r.supplierName)}>
                      <td className="sup-inn" style={{ fontWeight: 600 }}>{r.supplierName}</td>
                      <td>
                        {r.score != null ? (
                          <span className="sup-badge" style={{
                            background: r.score >= 60 ? 'var(--teal-soft, #e6f4f0)' : r.score >= 35 ? '#fff8e1' : '#fce4ec',
                            color: r.score >= 60 ? 'var(--teal, #0D5C4F)' : r.score >= 35 ? '#f57f17' : '#c62828',
                            fontWeight: 700, fontSize: 12, padding: '2px 8px', borderRadius: 6,
                          }}>{r.score}</span>
                        ) : '\u2014'}
                      </td>
                      <td><strong>{r.cumulative}</strong></td>
                      <td>{r.molecules}</td>
                      <td>{r.atcAreas}</td>
                      <td>{r.recentGrowth > 0 ? <span className="sup-badge teal-soft">+{r.recentGrowth}</span> : r.recentGrowth}</td>
                      <td>{fmtDate(r.firstSeen)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {filtered.length > 0 && (
            <div style={{ padding: '8px 14px', fontSize: 12, color: 'var(--t3)', borderTop: '1px solid #eef3f7' }}>
              {filtered.length} favorite{filtered.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
